import { useEffect, useMemo, useRef, useState, type FormEvent } from "react";
import Alert from "@mui/material/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Container from "@mui/material/Container";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Step from "@mui/material/Step";
import StepLabel from "@mui/material/StepLabel";
import Stepper from "@mui/material/Stepper";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { alpha, useTheme } from "@mui/material/styles";
import useMediaQuery from "@mui/material/useMediaQuery";
import {
  AtSign,
  ArrowRight,
  CheckCircle2,
  Circle,
  CircleAlert,
  Database,
  Eye,
  EyeOff,
  Images,
  KeyRound,
  LockKeyhole,
  RefreshCw,
  Server,
  ShieldCheck,
  UserRound,
  UserRoundCog
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "../components/ThemeToggle";
import { useApp } from "../context/AppContext";
import { ApiError } from "../lib/api";

type FieldName = "account" | "nickname" | "password" | "confirmation";
type FieldErrors = Partial<Record<FieldName, string>>;

const installationSteps = [
  { label: "环境检查", detail: "服务与数据库连接正常" },
  { label: "创建管理员", detail: "设置平台最高权限账号" },
  { label: "进入后台", detail: "继续配置内容与社群" }
];

const helperTextSx = { minHeight: 18, mt: 0.75 };

const assuranceItems = [
  {
    icon: Database,
    title: "数据库已就绪",
    detail: "管理员资料将写入当前平台数据库。"
  },
  {
    icon: LockKeyhole,
    title: "密码安全存储",
    detail: "密码仅以加密哈希保存，不会写入安装日志。"
  }
];

function SetupBrand() {
  return (
    <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
      <Box
        aria-hidden="true"
        sx={{
          display: "grid",
          width: 40,
          height: 40,
          placeItems: "center",
          borderRadius: 1,
          bgcolor: "primary.main",
          color: "primary.contrastText"
        }}
      >
        <Images size={21} strokeWidth={2.2} />
      </Box>
      <Box>
        <Typography component="strong" sx={{ display: "block", fontSize: 15, fontWeight: 780, lineHeight: 1.3 }}>
          共享朋友圈
        </Typography>
        <Typography component="span" sx={{ display: "block", mt: 0.125, color: "text.secondary", fontSize: 11, lineHeight: 1.3 }}>
          平台初始化
        </Typography>
      </Box>
    </Stack>
  );
}

function SetupHeader() {
  return (
    <Box component="header" sx={{ borderBottom: 1, borderColor: "divider", bgcolor: "background.paper" }}>
      <Container
        maxWidth="lg"
        sx={{ display: "flex", minHeight: 68, alignItems: "center", justifyContent: "space-between", gap: 2 }}
      >
        <SetupBrand />
        <Stack direction="row" spacing={1} sx={{ alignItems: "center" }}>
          <Chip
            icon={<ShieldCheck size={15} />}
            label="安全初始化"
            variant="outlined"
            sx={{ display: { xs: "none", sm: "inline-flex" }, color: "text.secondary", borderColor: "divider" }}
          />
          <ThemeToggle />
        </Stack>
      </Container>
    </Box>
  );
}

function SetupProgress() {
  const theme = useTheme();
  const compact = useMediaQuery(theme.breakpoints.down("md"));

  return (
    <Box component="section" aria-labelledby="setup-progress-title">
      <Stack direction="row" sx={{ mb: compact ? 1.75 : 1.25, alignItems: "center", justifyContent: "space-between" }}>
        <Typography id="setup-progress-title" component="h2" variant="overline" sx={{ color: "text.secondary" }}>
          安装进度
        </Typography>
        <Typography variant="caption" sx={{ color: "text.secondary", fontVariantNumeric: "tabular-nums" }}>
          2 / 3
        </Typography>
      </Stack>
      <Stepper
        activeStep={1}
        orientation={compact ? "horizontal" : "vertical"}
        alternativeLabel={compact}
        sx={{
          maxWidth: compact ? "none" : 440,
          "& .MuiStep-root": { px: compact ? 0.5 : 0 },
          "& .MuiStepLabel-label": {
            mt: compact ? 0.75 : 0,
            fontSize: compact ? 11 : 14,
            fontWeight: 720,
            whiteSpace: compact ? "nowrap" : "normal"
          },
          "& .MuiStepLabel-labelContainer .MuiTypography-caption": {
            display: compact ? "none" : "block",
            mt: 0.25,
            color: "text.secondary"
          },
          "& .MuiStepConnector-line": {
            minHeight: compact ? 0 : 26,
            borderColor: "divider"
          },
          "& .MuiStepIcon-root": { fontSize: compact ? 24 : 25 }
        }}
      >
        {installationSteps.map((step, index) => (
          <Step key={step.label} completed={index === 0}>
            <StepLabel optional={<Typography variant="caption">{step.detail}</Typography>}>
              {step.label}
            </StepLabel>
          </Step>
        ))}
      </Stepper>
    </Box>
  );
}

function SetupAssurance() {
  return (
    <Stack component="aside" aria-label="初始化安全说明" spacing={1.5}>
      <Typography component="h2" variant="overline" sx={{ color: "text.secondary" }}>
        安全边界
      </Typography>
      <Stack divider={<Divider flexItem />} spacing={1.5}>
        {assuranceItems.map(({ icon: Icon, title, detail }) => (
          <Stack key={title} direction="row" spacing={1.25} sx={{ alignItems: "flex-start" }}>
            <Box
              aria-hidden="true"
              sx={{
                display: "grid",
                width: 34,
                height: 34,
                flex: "0 0 auto",
                placeItems: "center",
                border: 1,
                borderColor: "divider",
                borderRadius: 1,
                bgcolor: "background.paper",
                color: "text.secondary"
              }}
            >
              <Icon size={16} />
            </Box>
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 720 }}>{title}</Typography>
              <Typography variant="caption" sx={{ display: "block", mt: 0.25, color: "text.secondary" }}>{detail}</Typography>
            </Box>
          </Stack>
        ))}
      </Stack>
    </Stack>
  );
}

function validateFields(account: string, nickname: string, password: string, confirmation: string) {
  const errors: FieldErrors = {};
  const normalizedAccount = account.trim();
  const normalizedNickname = nickname.trim();

  if (normalizedAccount.length < 3 || normalizedAccount.length > 32) {
    errors.account = "管理员账号需为 3-32 个字符";
  } else if (!/^[a-zA-Z0-9_.-]+$/.test(normalizedAccount)) {
    errors.account = "仅支持字母、数字、点、下划线和短横线";
  }
  if (normalizedNickname.length < 2 || normalizedNickname.length > 24) {
    errors.nickname = "显示名称需为 2-24 个字符";
  }
  if (password.length < 12 || password.length > 72) {
    errors.password = "管理员密码需为 12-72 个字符";
  }
  if (!confirmation) {
    errors.confirmation = "请再次输入管理员密码";
  } else if (password !== confirmation) {
    errors.confirmation = "两次输入的密码不一致";
  }

  return errors;
}

export function InstallPage() {
  const { completeSetup } = useApp();
  const navigate = useNavigate();
  const theme = useTheme();
  const errorRef = useRef<HTMLDivElement>(null);
  const accountInputRef = useRef<HTMLInputElement>(null);
  const nicknameInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const confirmationInputRef = useRef<HTMLInputElement>(null);
  const [account, setAccount] = useState("");
  const [nickname, setNickname] = useState("平台管理员");
  const [password, setPassword] = useState("");
  const [confirmation, setConfirmation] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldErrors>({});

  useEffect(() => {
    const previousTitle = document.title;
    document.title = "初始化平台 · 共享朋友圈";
    return () => { document.title = previousTitle; };
  }, []);

  useEffect(() => {
    if (error) errorRef.current?.focus();
  }, [error]);

  const passwordChecks = useMemo(() => ({
    length: password.length >= 12 && password.length <= 72,
    match: confirmation.length > 0 && password === confirmation
  }), [confirmation, password]);

  const clearFieldError = (field: FieldName) => {
    setError(null);
    setFieldErrors((current) => ({ ...current, [field]: undefined }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    const nextErrors = validateFields(account, nickname, password, confirmation);
    setFieldErrors(nextErrors);
    const firstInvalidField = (Object.keys(nextErrors) as FieldName[])[0];
    if (firstInvalidField) {
      const inputRefs = {
        account: accountInputRef,
        nickname: nicknameInputRef,
        password: passwordInputRef,
        confirmation: confirmationInputRef
      };
      inputRefs[firstInvalidField].current?.focus();
      return;
    }

    setBusy(true);
    try {
      await completeSetup(account.trim(), nickname.trim(), password);
      navigate("/admin", { replace: true });
    } catch (submitError) {
      if (submitError instanceof ApiError) setFieldErrors(submitError.fields ?? {});
      setError(submitError instanceof Error ? submitError.message : "安装未完成，请稍后重试");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box
      component="main"
      className="setup-page"
      sx={{ display: "flex", minHeight: "100dvh", flexDirection: "column", bgcolor: "background.default" }}
    >
      <SetupHeader />
      <Container
        maxWidth="lg"
        sx={{ display: "flex", flex: 1, alignItems: { md: "center" }, py: { xs: 3, sm: 5, md: 6 } }}
      >
        <Box
          sx={{
            display: "grid",
            width: "100%",
            gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "minmax(0, .82fr) minmax(470px, 1.18fr)" },
            gridTemplateAreas: {
              xs: '"intro" "progress" "form" "assurance"',
              md: '"intro form" "progress form" "assurance form"'
            },
            alignItems: "start",
            columnGap: { md: 7, lg: 9 },
            rowGap: { xs: 3, md: 3.25 }
          }}
        >
          <Box component="section" aria-labelledby="setup-title" sx={{ gridArea: "intro", pt: { md: 1 } }}>
            <Chip icon={<ShieldCheck size={15} />} label="首次安装 · 仅开放一次" color="primary" variant="outlined" />
            <Typography
              id="setup-title"
              component="h1"
              sx={{
                mt: { xs: 1.75, sm: 2.25 },
                maxWidth: 520,
                fontSize: { xs: 29, sm: 36, md: 40 },
                fontWeight: 780,
                lineHeight: 1.18
              }}
            >
              创建平台的第一位管理员
            </Typography>
            <Typography sx={{ mt: 1.5, maxWidth: 540, color: "text.secondary", fontSize: { xs: 14, sm: 15 } }}>
              设置用于管理平台的初始账号。完成后将直接进入后台，安装入口会自动关闭。
            </Typography>
          </Box>

          <Box sx={{ gridArea: "progress" }}>
            <SetupProgress />
          </Box>

          <Paper
            component="section"
            variant="outlined"
            aria-labelledby="admin-form-title"
            sx={{
              gridArea: "form",
              p: { xs: 2.25, sm: 3.25 },
              borderColor: "divider",
              borderTopWidth: 3,
              borderTopColor: "primary.main",
              boxShadow: theme.palette.mode === "dark" ? "none" : "0 18px 48px rgba(29,36,51,.08)",
              "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.mode === "dark" ? "#6b7280" : "#858b98" },
              "& .MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline": { borderColor: "text.secondary" },
              "& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: "primary.main" }
            }}
          >
            <Stack direction="row" spacing={1.25} sx={{ alignItems: "center" }}>
              <Box
                aria-hidden="true"
                sx={{
                  display: "grid",
                  width: 44,
                  height: 44,
                  flex: "0 0 auto",
                  placeItems: "center",
                  borderRadius: 1,
                  bgcolor: alpha(theme.palette.primary.main, 0.1),
                  color: "primary.main"
                }}
              >
                <UserRoundCog size={21} />
              </Box>
              <Stack direction="row" sx={{ minWidth: 0, flex: 1, alignItems: "center", justifyContent: "space-between", gap: 1 }}>
                <Box>
                  <Typography id="admin-form-title" component="h2" variant="h3">创建初始管理员</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>此账号将拥有平台最高管理权限。</Typography>
                </Box>
                <Chip label="必填" size="small" variant="outlined" sx={{ display: { xs: "none", sm: "inline-flex" } }} />
              </Stack>
            </Stack>

            {error ? (
              <Alert ref={errorRef} tabIndex={-1} severity="error" icon={<CircleAlert size={19} />} sx={{ mt: 2.5 }}>
                {error}
              </Alert>
            ) : null}

            <Box component="form" noValidate onSubmit={(event) => void submit(event)} aria-busy={busy} sx={{ mt: 2.75 }}>
              <Stack spacing={2}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <TextField
                    inputRef={accountInputRef}
                    label="管理员账号"
                    value={account}
                    onChange={(event) => { setAccount(event.target.value); clearFieldError("account"); }}
                    placeholder="例如 platform_admin"
                    autoFocus
                    required
                    disabled={busy}
                    error={Boolean(fieldErrors.account)}
                    helperText={fieldErrors.account || "3-32 个字符，仅支持字母、数字及 _.-"}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start"><AtSign size={18} aria-hidden="true" /></InputAdornment> },
                      htmlInput: { minLength: 3, maxLength: 32, autoComplete: "username", spellCheck: false },
                      formHelperText: { sx: helperTextSx }
                    }}
                  />
                  <TextField
                    inputRef={nicknameInputRef}
                    label="显示名称"
                    value={nickname}
                    onChange={(event) => { setNickname(event.target.value); clearFieldError("nickname"); }}
                    required
                    disabled={busy}
                    error={Boolean(fieldErrors.nickname)}
                    helperText={fieldErrors.nickname || "2-24 个字符，将显示在管理记录中"}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start"><UserRound size={18} aria-hidden="true" /></InputAdornment> },
                      htmlInput: { minLength: 2, maxLength: 24, autoComplete: "nickname" },
                      formHelperText: { sx: helperTextSx }
                    }}
                  />
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" }, gap: 2 }}>
                  <TextField
                    inputRef={passwordInputRef}
                    label="管理员密码"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(event) => { setPassword(event.target.value); clearFieldError("password"); }}
                    required
                    disabled={busy}
                    error={Boolean(fieldErrors.password)}
                    helperText={fieldErrors.password || "使用 12-72 个字符的独立密码"}
                    slotProps={{
                      input: {
                        startAdornment: <InputAdornment position="start"><KeyRound size={18} aria-hidden="true" /></InputAdornment>,
                        endAdornment: (
                          <InputAdornment position="end">
                            <Tooltip title={showPassword ? "隐藏密码" : "显示密码"}>
                              <IconButton
                                edge="end"
                                type="button"
                                disabled={busy}
                                aria-label={showPassword ? "隐藏密码" : "显示密码"}
                                onClick={() => setShowPassword((value) => !value)}
                              >
                                {showPassword ? <EyeOff size={18} aria-hidden="true" /> : <Eye size={18} aria-hidden="true" />}
                              </IconButton>
                            </Tooltip>
                          </InputAdornment>
                        )
                      },
                      htmlInput: { minLength: 12, maxLength: 72, autoComplete: "new-password" },
                      formHelperText: { sx: helperTextSx }
                    }}
                  />

                  <TextField
                    inputRef={confirmationInputRef}
                    label="确认密码"
                    type={showPassword ? "text" : "password"}
                    value={confirmation}
                    onChange={(event) => { setConfirmation(event.target.value); clearFieldError("confirmation"); }}
                    required
                    disabled={busy}
                    error={Boolean(fieldErrors.confirmation)}
                    helperText={fieldErrors.confirmation || "再次输入密码以确认"}
                    slotProps={{
                      input: { startAdornment: <InputAdornment position="start"><LockKeyhole size={18} aria-hidden="true" /></InputAdornment> },
                      htmlInput: { minLength: 12, maxLength: 72, autoComplete: "new-password" },
                      formHelperText: { sx: helperTextSx }
                    }}
                  />
                </Box>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={{ xs: 0.75, sm: 2.5 }} aria-live="polite">
                  {[
                    { valid: passwordChecks.length, label: "密码长度符合要求" },
                    { valid: passwordChecks.match, label: "两次密码输入一致" }
                  ].map((check) => (
                    <Stack key={check.label} direction="row" spacing={0.75} sx={{ alignItems: "center", color: check.valid ? "success.main" : "text.secondary" }}>
                      {check.valid ? <CheckCircle2 size={16} aria-hidden="true" /> : <Circle size={16} aria-hidden="true" />}
                      <Typography variant="caption" sx={{ color: "inherit" }}>{check.label}</Typography>
                    </Stack>
                  ))}
                </Stack>

                <Alert severity="info" icon={<Server size={19} />}>
                  本次操作只创建初始管理员，不会修改已有业务数据。完成后安装入口将立即关闭。
                </Alert>

                <Button type="submit" variant="contained" size="large" fullWidth disabled={busy} sx={{ minHeight: 50, mt: 0.25 }}>
                  {busy ? <><CircularProgress size={18} color="inherit" />正在完成安装</> : <>完成安装并进入后台<ArrowRight aria-hidden="true" /></>}
                </Button>
              </Stack>
            </Box>

            <Divider sx={{ my: 2.5 }} />
            <Typography variant="caption" sx={{ display: "block", color: "text.secondary", textAlign: "center" }}>
              后续管理员账号请在管理后台的用户管理中创建。
            </Typography>
          </Paper>

          <Box sx={{ gridArea: "assurance", maxWidth: 500, pt: { md: 0.5 } }}>
            <SetupAssurance />
          </Box>
        </Box>
      </Container>
    </Box>
  );
}

export function SetupLoadingPage() {
  return (
    <Box component="main" className="setup-boot" sx={{ display: "flex", minHeight: "100dvh", flexDirection: "column", bgcolor: "background.default" }}>
      <SetupHeader />
      <Container sx={{ display: "grid", flex: 1, placeItems: "center", py: 4 }}>
        <Paper variant="outlined" role="status" aria-live="polite" sx={{ width: "min(100%, 420px)", p: 4, textAlign: "center" }}>
          <CircularProgress size={28} thickness={4} />
          <Typography component="h1" variant="h3" sx={{ mt: 2.25 }}>正在检查安装状态</Typography>
          <Typography variant="body2" sx={{ mt: 1, color: "text.secondary" }}>正在连接平台服务，请稍候。</Typography>
        </Paper>
      </Container>
    </Box>
  );
}

export function SetupUnavailablePage({ message, onRetry }: { message: string | null; onRetry: () => void }) {
  const theme = useTheme();

  return (
    <Box component="main" className="setup-unavailable" sx={{ display: "flex", minHeight: "100dvh", flexDirection: "column", bgcolor: "background.default" }}>
      <SetupHeader />
      <Container sx={{ display: "grid", flex: 1, placeItems: "center", py: 4 }}>
        <Paper variant="outlined" sx={{ width: "min(100%, 460px)", p: { xs: 3, sm: 4 }, textAlign: "center" }}>
          <Box
            aria-hidden="true"
            sx={{ display: "grid", width: 52, height: 52, mx: "auto", placeItems: "center", borderRadius: 1, bgcolor: alpha(theme.palette.error.main, 0.1), color: "error.main" }}
          >
            <CircleAlert size={24} />
          </Box>
          <Typography component="h1" variant="h3" sx={{ mt: 2.25 }}>暂时无法读取安装状态</Typography>
          <Typography sx={{ mt: 1, color: "text.secondary", overflowWrap: "anywhere" }}>
            {message || "请确认平台服务已启动，然后重新检查。"}
          </Typography>
          <Button type="button" variant="contained" onClick={onRetry} startIcon={<RefreshCw size={18} />} sx={{ mt: 3 }}>
            重新检查
          </Button>
        </Paper>
      </Container>
    </Box>
  );
}
